import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { InputsContext } from './Context';
import { isIOS } from '../../lib/userAgent';
import getUserMedia from '../../lib/getUserMedia';

const LOCAL_STORAGE_INPUT_CONFIGS_KEY = 'beeyou_inputDevices';
const DEFAULT_CONFIG_ID = 0;
const DEFAULT_CONFIG_LABEL = `Source ${DEFAULT_CONFIG_ID + 1}`;
const INPUT_CONFIG_VERSION = 6;

const getSavedInputsConfig = () => {
	const savedConfig = localStorage.getItem(LOCAL_STORAGE_INPUT_CONFIGS_KEY);

	if (savedConfig) {
		const config = JSON.parse(savedConfig);

		// eslint-disable-next-line no-underscore-dangle
		if (config.__version !== INPUT_CONFIG_VERSION) {
			return undefined;
		}

		if (isIOS) {
			// iOS doesn't allow to have multiple cameras enabled at the same time
			return config.inputsConfig.filter((cfg) => cfg.id === DEFAULT_CONFIG_ID);
		}

		return config.inputsConfig;
	}
	return undefined;
};

const saveInputsConfig = (inputsConfig) => localStorage.setItem(
	LOCAL_STORAGE_INPUT_CONFIGS_KEY,
	JSON.stringify({ inputsConfig, __version: INPUT_CONFIG_VERSION }),
);

const DEFAULT_DEVICE_ID = 'default';
const DEFAULT_DEVICE_LABEL = 'Default';

export const DEFAULT_DEVICES = {
	cam: {
		deviceId: 'default-cam',
		label: 'Default camera',
	},
	mic: {
		deviceId: 'default-mic',
		label: 'Default microphone',
	},
};

const DEFAULT_INPUT_CONFIG = {
	label: DEFAULT_CONFIG_LABEL,
	id: DEFAULT_CONFIG_ID,
	audioInputOff: false,
	videoInputOff: false,
	audioInputId: DEFAULT_DEVICES.mic.deviceId,
	audioDeviceLabel: DEFAULT_DEVICES.mic.label,
	videoInputId: DEFAULT_DEVICES.cam.deviceId,
	videoDeviceLabel: DEFAULT_DEVICES.cam.label,
};

const findDeviceByDeviceId = (devices, deviceId) => {
	if (
		deviceId === DEFAULT_DEVICES.cam.deviceId
		|| deviceId === DEFAULT_DEVICES.mic.deviceId
	) {
		return devices[0];
	}
	return devices.find((device) => device.deviceId === deviceId);
};

export const Inputs = ({
	children,
}) => {
	const [enabled, setEnabled] = useState(false);
	const savedInputsConfig = useMemo(() => getSavedInputsConfig(), []);

	const [inputDevices, setInputDevices] = useState([]);
	const audioInputDevices = useMemo(() => inputDevices.filter((device) => device.kind === 'audioinput'), [inputDevices]);
	const videoInputDevices = useMemo(() => inputDevices.filter((device) => device.kind === 'videoinput'), [inputDevices]);

	const [inputsConfig, setInputsConfig] = useState(
		savedInputsConfig?.length ? savedInputsConfig : [DEFAULT_INPUT_CONFIG],
	);

	const currentlyEnabledDefaultDevices = useMemo(() => {
		const defaultInputConfig = inputsConfig[0];
		const enabledDefaultDevices = [];
		if (!defaultInputConfig) {
			return enabledDefaultDevices;
		}
		if (defaultInputConfig.audioInputId && !defaultInputConfig.audioInputOff) {
			enabledDefaultDevices.push(
				findDeviceByDeviceId(audioInputDevices, defaultInputConfig.audioInputId),
			);
		}
		if (defaultInputConfig.videoInputId && !defaultInputConfig.videoInputOff) {
			enabledDefaultDevices.push(
				findDeviceByDeviceId(videoInputDevices, defaultInputConfig.videoInputId),
			);
		}
		return enabledDefaultDevices.filter(Boolean);
	}, [audioInputDevices, inputsConfig, videoInputDevices]);

	const getAvailableInputId = useCallback((inputId, configId) => {
		if (!inputDevices?.length || !inputId) return undefined;

		let enabledInput;

		if (inputId === DEFAULT_DEVICES.cam.deviceId) {
			[enabledInput] = inputDevices.filter(({ kind }) => kind === 'videoinput');
		} else if (inputId === DEFAULT_DEVICES.mic.deviceId) {
			[enabledInput] = inputDevices.filter(({ kind }) => kind === 'audioinput');
		} else {
			let availableDevices = inputDevices;
			if (configId !== 0) {
				// In case the device is already enabled as "default" device,
				// don't start twice the same device
				availableDevices = inputDevices.filter(({ deviceId }) => (
					!currentlyEnabledDefaultDevices.find((device) => device.deviceId === deviceId)
				));
			}

			enabledInput = availableDevices.find(({ deviceId }) => deviceId === inputId);
		}

		return enabledInput?.deviceId;
	}, [currentlyEnabledDefaultDevices, inputDevices]);

	const helpers = useMemo(() => {
		const setPartialInputsConfig = (config) => {
			setInputsConfig((s) => (
				s.map((cfg) => {
					if (cfg.id !== config.id) {
						return cfg;
					}
					return {
						...cfg,
						...config,
					};
				})
			));
		};

		return {
			setPartialInputsConfig,
			addNewConfig: ({ label, id }) => {
				const firstAvailableAudioInputDevice = [...audioInputDevices]
					.filter((audioInputDevice) => !currentlyEnabledDefaultDevices
						.find((device) => device.deviceId === audioInputDevice.deviceId))
					.find(
						({ deviceId }) => !inputsConfig.find((c) => c.audioInputId === deviceId),
					);
				const firstAvailableVideoInputDevice = [...videoInputDevices]
					.filter((videoInputDevice) => !currentlyEnabledDefaultDevices
						.find((device) => device.deviceId === videoInputDevice.deviceId))
					.find(
						({ deviceId }) => !inputsConfig.find((c) => c.videoInputId === deviceId),
					);
				const config = {
					label,
					id,
					audioInputOff: false,
					videoInputOff: false,
					audioInputId: firstAvailableAudioInputDevice?.deviceId ?? undefined,
					audioDeviceLabel: firstAvailableAudioInputDevice?.label ?? undefined,
					videoInputId: firstAvailableVideoInputDevice?.deviceId ?? undefined,
					videoDeviceLabel: firstAvailableVideoInputDevice?.label ?? undefined,
				};
				setInputsConfig((s) => ([...s, { ...config }]));
			},
			activateAudioInput: (configId) => {
				setPartialInputsConfig({ id: configId, audioInputOff: false });
			},
			activateVideoInput: (configId) => {
				setPartialInputsConfig({ id: configId, videoInputOff: false });
			},
			deactivateAudioInput: (configId) => {
				setPartialInputsConfig({ id: configId, audioInputOff: true });
			},
			deactivateVideoInput: (configId) => {
				setPartialInputsConfig({ id: configId, videoInputOff: true });
			},
			deleteConfig: (configId) => {
				setInputsConfig((s) => s.filter((cfg) => cfg.id !== configId));
			},
			changeAudioInputDevice: (configId, id, audioDeviceLabel) => {
				setPartialInputsConfig({ id: configId, audioInputId: id, audioDeviceLabel });
			},
			changeVideoInputDevice: (configId, id, videoDeviceLabel) => {
				setPartialInputsConfig({ id: configId, videoInputId: id, videoDeviceLabel });
			},
			changeConfigName: (configId, name) => {
				setPartialInputsConfig({ id: configId, label: name });
			},
		};
	}, [
		audioInputDevices,
		currentlyEnabledDefaultDevices,
		inputsConfig,
		videoInputDevices,
	]);

	const isPermissonGranted = useRef(false);
	const updateInputDevices = useCallback(async () => {
		let devicesInfo = [];
		let mediastream;
		try {
			if (!isPermissonGranted.current) {
				isPermissonGranted.current = false;
				/* enumerateDevices() returns an incomplete result on Safari and Firefox
				until getUserMedia permission is granted.
				So we request user media just to execute enumerateDevices,
				then we stop the tracks to cancel the user media request. */
				mediastream = await getUserMedia({ audio: true, video: true });
				isPermissonGranted.current = true;
			}
			devicesInfo = await navigator.mediaDevices.enumerateDevices();
		} catch (err) {
			console.error(err);
		} finally {
			if (mediastream) {
				mediastream.getTracks().forEach((track) => track.stop());
			}
		}

		const isDefaultDevice = (device) => !!devicesInfo.find((d) => (
			d.groupId === device.groupId
			&& (
				d.deviceId === DEFAULT_DEVICE_ID
				|| d.deviceId === ''
			)
		));

		const isVirtualDevice = (device) => {
			const upperLabel = (device.label || '').toUpperCase();
			if (upperLabel.includes('OBS ')) return true;
			if (upperLabel.includes('VIRTUAL')) return true;
			return false;
		};

		const uniqueDevicesInfo = devicesInfo
			// Firefox fix duplicates video input devices
			.filter(
				(device, index, self) => index === self.findIndex((d) => (
					d.deviceId === device.deviceId
					&& d.kind === device.kind
				)),
			)
			/* removing default device because it's duplicated
			so it could be chosen in thow different input configs */
			.filter((deviceInfo) => (
				deviceInfo.deviceId !== DEFAULT_DEVICE_ID
				&& deviceInfo.deviceId !== ''
			))
			.sort((a, b) => {
				// Keep order of different devices
				if (a.kind !== b.kind) { return 0; }

				// Devices that have a duplicate which has a "default" deviceId are first
				let aScore = isDefaultDevice(a) ? 1 : 0;
				let bScore = isDefaultDevice(b) ? 1 : 0;

				const diff = bScore - aScore;
				if (diff !== 0) return diff;

				// Virtual devices are last
				aScore = isVirtualDevice(a) ? 0 : 1;
				bScore = isVirtualDevice(b) ? 0 : 1;

				return bScore - aScore;
			})
			.map((deviceInfo) => ({
				deviceId: deviceInfo.deviceId,
				groupId: deviceInfo.groupId,
				kind: deviceInfo.kind,
				label: deviceInfo.label || deviceInfo.deviceId || DEFAULT_DEVICE_LABEL,
			}));

		setInputDevices(uniqueDevicesInfo);
	}, []);

	useEffect(() => {
		const handleDeviceChange = () => { updateInputDevices(); };

		/* "enabled" flag to avoid asking permission on safari until the Media component is enabled.
		See Media component. */
		if (enabled) {
			if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
				navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
			}

			updateInputDevices();
		}

		return () => {
			if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
				navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
			}
		};
	}, [enabled, updateInputDevices]);

	// Store input config in localstorage
	useEffect(() => { saveInputsConfig(inputsConfig); }, [inputsConfig]);

	useEffect(() => {
		const defaultInputConfig = inputsConfig?.find(({ id }) => id === DEFAULT_CONFIG_ID);

		if (!defaultInputConfig) {
			setInputsConfig((s) => [DEFAULT_INPUT_CONFIG, ...s]);
		}
	}, [videoInputDevices, audioInputDevices, inputsConfig, helpers]);

	const value = useMemo(() => ({
		audioInputDevices,
		currentlyEnabledDefaultDevices,
		getAvailableInputId,
		videoInputDevices,
		...helpers,
		inputsConfig,
		setEnabled,
		setInputDevices,
		setInputsConfig,
		updateInputDevices,
	}), [
		audioInputDevices,
		currentlyEnabledDefaultDevices,
		getAvailableInputId,
		videoInputDevices,
		helpers,
		inputsConfig,
		updateInputDevices,
	]);

	return (
		<InputsContext.Provider value={value}>
			{children}
		</InputsContext.Provider>
	);
};

Inputs.propTypes = {
	children: PropTypes.node.isRequired,
};
